import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { first, partial } from 'lodash';
import Pagination from 'react-js-pagination';
import React, { useState, useRef, useEffect } from 'react';
import { faFileUpload } from '@fortawesome/pro-solid-svg-icons';

import { Media as MediaProps } from '~/types/api';
import createMediaApi from '~/App/Api/Media';
import SimpleMediaItem from './SimpleMediaItem';
import ListGroup from '~/App/Components/ListGroup';
import SnapButton from '~/App/Components/Buttons/SnapButton';
import { confirmAsync } from '~/App/Components/Modals/Confirm';
import { SpinnerNew as Spinner } from '~/App/Components/Shared/Spinner';
import Replaceable from '~/components/higher-order/ReplaceableComponents';

import { MediaPagination } from './MediaPicker';
import { LoadingProgress } from '@vimily/bonjoro-ui-components-v2/LoadingProgress';
import { useMutation } from 'react-query';
import { useUploadImageMedia } from '../hooks/use-upload-image';

const StyledListGroup = styled(ListGroup)`
  height: ${(props: { perPage: number }) => props.perPage * 71}px;
`;

const FileInput = styled.input`
  &&& {
    display: none;
  }
`;
FileInput.defaultProps = {
  type: 'file',
};

const Header = styled.h3`
  margin: 0;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const PaginationContainer = styled.div`
  display: flex;
  margin: 0 0 15px;
  align-items: center;
  flex-direction: column;

  ul.pagination {
    margin: 0;
  }
`;

export const components = {
  HeaderContainer,
  Header,
  UploadButton: SnapButton,
};

export interface SimpleMediaPickerProps {
  selectedId?: string;
  tags: string | string[];
  perPage: number;
  onItemDeleted: (media: MediaProps) => void;
  onChange: (media: Partial<MediaProps>) => void;
  components: {
    HeaderContainer: React.ReactType;
    Header: React.ReactType;
    UploadButton: React.ReactType;
  };
}

function SimpleMediaPicker({
  components,
  selectedId,
  tags,
  perPage = 5,
  onChange,
  onItemDeleted,
}: SimpleMediaPickerProps) {
  const [media, setMedia] = useState<MediaProps[]>();
  const [isLoading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<MediaPagination>({
    activePage: 1,
  });
  const mediaApi = createMediaApi();
  const [uploadProgress, setUploadProgress] = useState(0);

  const fileInput = useRef<HTMLInputElement>();

  const { mutateAsync: uploadMedia } = useMutation(useUploadImageMedia, {
    onSuccess: () => {
      if (fileInput.current) {
        fileInput.current.value = '';
      }
      loadMedia({ page: 1 });
      setUploadProgress(0);
    },
  });

  function getTags() {
    if (tags instanceof Array) {
      return tags.join(' ');
    }

    return tags;
  }

  const loadMedia = async (params = {}) => {
    setLoading(true);
    const res = await mediaApi.getAll({
      ...params,
      tags: getTags(),
      limit: perPage,
    });

    setMedia(res.data);
    setPagination({
      activePage: res.current_page,
      itemsCountPerPage: res.per_page,
      totalItemsCount: res.total,
      lastPage: res.last_page,
      count: res.data.length,
    });
    setLoading(false);
  };

  const handleDeleteClick = async (media) => {
    if (
      await confirmAsync(
        'Are you sure you want to delete this media file? Deletion is permanent!'
      )
    ) {
      setLoading(true);
      await mediaApi.delete(media.id);

      const page =
        pagination.activePage == pagination.lastPage && pagination?.count > 1
          ? pagination.activePage
          : pagination?.lastPage - 1;

      await loadMedia({ page });

      if (typeof onItemDeleted === 'function') {
        onItemDeleted(media);
      }
    }
  };

  const handleUploadClick = () => fileInput?.current?.click();

  const handleFileChange = () => {
    const file = first(fileInput?.current?.files);

    if (file == null) {
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('tags', getTags());

    setLoading(true);
    uploadMedia({ formData, onUploadProgress: setUploadProgress });
  };

  useEffect(() => {
    loadMedia({ page: pagination.activePage });
  }, []);

  const { HeaderContainer, Header, UploadButton } = components;

  return (
    <React.Fragment>
      <HeaderContainer>
        <Header>
          {'Media Library '}
          {!isLoading || <Spinner />}
        </Header>
        <UploadButton
          iconBefore={faFileUpload}
          onClick={handleUploadClick}
          disabled={isLoading}
        >
          Upload new file
        </UploadButton>
      </HeaderContainer>
      <StyledListGroup perPage={perPage}>
        {fileInput.current?.value && (
          <LoadingProgress
            progress={uploadProgress}
            className='h-[20px] mb-4'
          />
        )}
        {media &&
          media.map((media: MediaProps) => {
            return (
              <SimpleMediaItem
                key={media.id}
                media={media}
                active={selectedId == media.id}
                onClick={() => onChange(media)}
                onDeleteClick={partial(handleDeleteClick, media)}
              />
            );
          })}
      </StyledListGroup>
      <PaginationContainer>
        <Pagination
          {...pagination}
          onChange={(page) => loadMedia({ page })}
          pageRangeDisplayed={5}
        />
      </PaginationContainer>
      <FileInput ref={fileInput} onChange={handleFileChange} accept='image/*' />
    </React.Fragment>
  );
}

SimpleMediaPicker.propTypes = {
  perPage: PropTypes.number,
  selectedId: PropTypes.string,
  tags: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.string,
  ]).isRequired,
  components: PropTypes.any,

  // handlers
  onChange: PropTypes.func,
  onItemDeleted: PropTypes.func,
};

export default Replaceable(components)(SimpleMediaPicker);
